import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Chip,
  Select,
  Pagination,
  Tabs,
  Tab,
  MenuItem,
  IconButton,
  Menu,
} from '@mui/material';
import ReceiptOutlined from '@mui/icons-material/ReceiptOutlined';
import { styled } from '@mui/material/styles';
import { billingService, BillingRecord, BillingSubscriptionStatus } from '@services/billing';
import {
  getPaymentMethodUpdateReturnUrls,
  startUpdatePaymentMethod,
} from '@services/payment';
import { submitFacilityCancellation, submitFacilityResubscribe } from '@services/facilityManagement';
import { auditLogService } from '@services/auditLog';
import { getCurrentUserId } from '@utils/userUtils';
import { getSelectedFacilityFromSession } from '@utils/facilityUtils';
import { navigateToRestrictedFacility } from '@utils/facilityRestricted';
import { useFacilityLayoutDataOptional } from '@context';
import { toast } from 'react-toastify';
import ReviewSubscriptionDialog from './ReviewSubscriptionDialog';
import ConfirmCancellationDialog from './ConfirmCancellationDialog';
import SubscriptionCancelledDialog from './SubscriptionCancelledDialog';
import ResubscribeDialog from './ResubscribeDialog';
import WelcomeBackDialog from './WelcomeBackDialog';
import AddonsList from './AddonsList';
import AddonDetail from './AddonDetail';
import TierPicker from './TierPicker';
import TierReview from './TierReview';
import AddonPurchaseSuccess from './AddonPurchaseSuccess';
import { getBillingAddonsSettings } from '@services/billingAddonsSettings';
// Custom SVG Icons as components
const MoreHorizIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="3" cy="8" r="1.5" fill="currentColor" />
    <circle cx="8" cy="8" r="1.5" fill="currentColor" />
    <circle cx="13" cy="8" r="1.5" fill="currentColor" />
  </svg>
);

/** Temporary: block Cancel Subscription until re-enabled */
const CANCEL_SUBSCRIPTION_DISABLED = false;

/** Same title band as Profile / Regulations Library (Discovery 80px + 20px). */
const PAGE_TITLE_BAND_HEIGHT_PX = 100;

/** Discovery-style shell: fixed card height, clip overflow, scroll inside ContentContainer. */
const PageContainer = styled(Box)({
  backgroundColor: '#fdffff',
  borderRadius: '12px',
  height: '100%',
  minHeight: 0,
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  position: 'relative',
  width: '100%',
  boxSizing: 'border-box',
  border: '1px solid #99d4e1',
  boxShadow: 'inset 8px 2px 58.4px 0px rgba(191,242,255,0.2)',
});

/** Title band pinned by flex (not viewport sticky) — same look as Profile / Discovery. */
const HeaderContainer = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(253, 255, 255, 0.2)',
  borderBottom: '1px solid #35d7e4',
  padding: '0 64px 24px',
  height: `${PAGE_TITLE_BAND_HEIGHT_PX}px`,
  minHeight: `${PAGE_TITLE_BAND_HEIGHT_PX}px`,
  display: 'flex',
  alignItems: 'flex-end',
  flexShrink: 0,
  boxSizing: 'border-box',
  [theme.breakpoints.down('md')]: {
    padding: '0 16px 16px',
    height: 'auto',
    minHeight: `${PAGE_TITLE_BAND_HEIGHT_PX}px`,
  },
}));

const HeaderTitleRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const HeaderIconWrapper = styled(Box)({
  width: '28px',
  height: '28px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
});

const HeaderIcon = styled(ReceiptOutlined)({
  fontSize: '28px',
  color: '#065b70',
});

const Title = styled(Typography)(({ theme }) => ({
  fontFamily: 'Rethink Sans, sans-serif',
  fontWeight: 500,
  fontSize: '28px',
  lineHeight: 1.5,
  letterSpacing: '-1px',
  color: '#262626',
  textAlign: 'left',
  margin: 0,
  [theme.breakpoints.down('sm')]: {
    fontSize: '22px',
  },
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  padding: '16px 48px 24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  overflowY: 'auto',
  overflowX: 'hidden',
  boxSizing: 'border-box',
  [theme.breakpoints.down('md')]: {
    padding: '12px 16px 24px',
  },
}));

const TableContainer = styled(Box)({
  backgroundColor: '#f8fdfe',
  border: '1px solid #cff7ff',
  borderRadius: '8px',
  padding: '8px',
  overflowX: 'auto',
  overflowY: 'visible',
});

const TABLE_ACTION_COLUMN_WIDTH = '72px';

const actionHeaderSx = {
  flex: `0 0 ${TABLE_ACTION_COLUMN_WIDTH}`,
  minWidth: TABLE_ACTION_COLUMN_WIDTH,
  width: TABLE_ACTION_COLUMN_WIDTH,
  maxWidth: TABLE_ACTION_COLUMN_WIDTH,
  justifyContent: 'center',
};

const actionCellSx = {
  flex: `0 0 ${TABLE_ACTION_COLUMN_WIDTH}`,
  minWidth: TABLE_ACTION_COLUMN_WIDTH,
  width: TABLE_ACTION_COLUMN_WIDTH,
  maxWidth: TABLE_ACTION_COLUMN_WIDTH,
  justifyContent: 'center',
};

const Table = styled(Box)({
  border: '1px solid #0a829d',
  borderRadius: '4px',
  overflow: 'hidden',
  width: '100%',
  minWidth: 0,
});

const TableHeader = styled(Box)({
  backgroundColor: '#0a829d',
  display: 'flex',
  alignItems: 'stretch',
  padding: 0,
  minHeight: '48px',
});

const TableHeaderCell = styled(Box)<{ width?: string }>(({ width }) => ({
  padding: '10px 8px',
  fontFamily: 'Rethink Sans, sans-serif',
  fontSize: '13px',
  fontWeight: 500,
  lineHeight: 1.35,
  color: '#ffffff',
  width: width || 'auto',
  flex: width ? `1 1 ${width}` : '1 1 auto',
  flexShrink: 1,
  minWidth: 0,
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  whiteSpace: 'normal',
  wordBreak: 'break-word',
}));

const TableRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  padding: 0,
  minHeight: '38px',
  borderBottom: '1px solid #e5f5f8',
  gap: 0,
  '&:last-child': {
    borderBottom: 'none',
  },
});

const TableCell = styled(Box)<{ width?: string }>(({ width }) => ({
  padding: '6px 8px',
  fontFamily: 'Rethink Sans, sans-serif',
  fontSize: '12px',
  color: '#262626',
  width: width || 'auto',
  flex: width ? `1 1 ${width}` : '1 1 auto',
  flexShrink: 1,
  minWidth: 0,
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const ActionButton = styled(IconButton)({
  width: 24,
  height: 24,
  padding: 2,
  color: '#0a829d',
});

const PlanText = styled(Typography)({
  fontSize: '12px',
  fontWeight: 600,
  color: '#065b70',
  fontFamily: 'Rethink Sans, sans-serif',
});

const StatusChip = styled(Chip)<{
  statusvariant?: 'active' | 'paymentRequired' | 'cancelling';
}>(({ statusvariant }) => {
  if (statusvariant === 'active') {
    return {
      backgroundColor: '#dff6dd',
      border: '1px solid #107c10',
      borderRadius: '24px',
      padding: '2px 12px',
      height: 'auto',
      '& .MuiChip-label': {
        color: '#107c10',
        fontSize: '12px',
        fontWeight: 400,
        fontFamily: 'Rethink Sans, sans-serif',
        padding: 0,
        lineHeight: 1.5,
      },
    };
  }

  if (statusvariant === 'cancelling') {
    return {
      backgroundColor: '#fdf3e5',
      border: '1px solid #bc7a2d',
      borderRadius: '24px',
      padding: '2px 12px',
      height: 'auto',
      maxWidth: '100%',
      '& .MuiChip-label': {
        color: '#8c5d1f',
        fontSize: '12px',
        fontWeight: 400,
        fontFamily: 'Rethink Sans, sans-serif',
        padding: 0,
        lineHeight: 1.5,
        whiteSpace: 'normal',
      },
    };
  }

  return {
    backgroundColor: '#fde7e9',
    border: '1px solid #d13438',
    borderRadius: '24px',
    padding: '2px 12px',
    height: 'auto',
    '& .MuiChip-label': {
      color: '#a4262c',
      fontSize: '12px',
      fontWeight: 400,
      fontFamily: 'Rethink Sans, sans-serif',
      padding: 0,
      lineHeight: 1.5,
    },
  };
});

interface BillingPageProps {}

function formatCancelChipDate(value: string | null | undefined): string {
  if (!value || value === '—') return '—';
  const parts = value.split('/');
  if (parts.length === 3) {
    const m = parseInt(parts[0], 10);
    const d = parseInt(parts[1], 10);
    const y = parseInt(parts[2], 10);
    const dt = new Date(y, m - 1, d);
    if (!Number.isNaN(dt.getTime())) {
      return dt.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    }
  }
  const fallback = new Date(value);
  if (!Number.isNaN(fallback.getTime())) {
    return fallback.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }
  return value;
}

function isApiCancelledRow(raw: Record<string, unknown>): boolean {
  const rawStatus = String(raw.status ?? raw.apiStatus ?? raw._rawStatus ?? '')
    .toLowerCase()
    .trim();
  return (
    rawStatus.includes('cancel') ||
    rawStatus === 'pending_cancellation' ||
    rawStatus === 'cancelling' ||
    Number(raw.is_cancelled) === 1 ||
    raw.is_cancelled === true ||
    Number(raw.cancel_at_period_end) === 1 ||
    raw.cancel_at_period_end === true ||
    Boolean(raw.cancellation_date) ||
    Boolean(raw.cancellation_end_date) ||
    Boolean(raw.cancel_at)
  );
}

function cancellationDateFromRecord(record: BillingRecord): string | null {
  const raw = record.rawPlanRow ?? {};
  const value =
    record.cancellationDate ??
    raw.cancellation_date ??
    raw.cancellation_end_date ??
    raw.cancel_at ??
    record.planEndDate ??
    record.planEndDateIso ??
    record.expiryDate;
  return value != null && String(value).trim() !== '' ? String(value) : null;
}

function withCancelledApiFields(
  record: BillingRecord,
  cancellationDate: string,
): BillingRecord {
  return {
    ...record,
    apiStatus: 'cancelled',
    isCancelled: true,
    cancelAtPeriodEnd: true,
    cancellationDate,
    rawPlanRow: {
      ...(record.rawPlanRow ?? {}),
      status: 'cancelled',
      cancellation_date: cancellationDate,
      cancel_at_period_end: 1,
      is_cancelled: 1,
    },
  };
}

function resolveSubscriptionStatus(record: BillingRecord): BillingRecord {
  const raw = record.rawPlanRow ?? {};
  const apiCancelled = isApiCancelledRow({
    status: record.apiStatus ?? raw.status ?? record.status,
    apiStatus: record.apiStatus,
    _rawStatus: (record as BillingRecord & { _rawStatus?: string })._rawStatus,
    is_cancelled: record.isCancelled ?? raw.is_cancelled,
    cancel_at_period_end: record.cancelAtPeriodEnd ?? raw.cancel_at_period_end,
    cancellation_date: record.cancellationDate ?? raw.cancellation_date,
    cancellation_end_date: raw.cancellation_end_date,
    cancel_at: raw.cancel_at,
  });

  if (apiCancelled) {
    const cancelDateRaw = cancellationDateFromRecord(record);
    return {
      ...record,
      subscriptionStatus: 'Cancelling',
      cancelsOnLabel: formatCancelChipDate(cancelDateRaw ?? record.expiryDate),
    };
  }

  const subscriptionStatus: BillingSubscriptionStatus =
    record.status === 'Active' ? 'Active' : 'Payment Required';

  return {
    ...record,
    subscriptionStatus,
    cancelsOnLabel: undefined,
  };
}

function applySubscriptionDisplayState(records: BillingRecord[]): BillingRecord[] {
  return records.map((record) => resolveSubscriptionStatus(record));
}

function formatPaymentMethodDisplay(value: string | undefined): string {
  if (!value) return '—';
  const trimmed = value.trim();
  if (/^\d{4}$/.test(trimmed)) return `**** ${trimmed}`;
  if (trimmed.includes('****') || trimmed.includes('—') || trimmed.includes('-')) {
    return trimmed.replace(/\s+/g, ' ');
  }
  const digits = trimmed.match(/(\d{4})$/)?.[1];
  const brand = trimmed.replace(/\d+/g, '').trim() || 'CARD';
  return digits ? `${brand} **** ${digits}` : trimmed;
}

/** Parse MM/DD/YYYY from API mapping; return long date e.g. "10 April 2026". */
function formatLongDate(value: string): string {
  if (!value || value === '—') return '—';
  const parts = value.split('/');
  if (parts.length === 3) {
    const m = parseInt(parts[0], 10);
    const d = parseInt(parts[1], 10);
    const y = parseInt(parts[2], 10);
    const dt = new Date(y, m - 1, d);
    if (!Number.isNaN(dt.getTime())) {
      return dt.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    }
  }
  const fallback = new Date(value);
  if (!Number.isNaN(fallback.getTime())) {
    return fallback.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }
  return value;
}

function pickNumericId(value: unknown): number | undefined {
  if (value == null || value === '') return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function parseApiDate(value: string | undefined | null): Date | null {
  if (!value) return null;
  const trimmed = String(value).trim();
  const isoDateOnly = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
  if (isoDateOnly) {
    return new Date(
      Number(isoDateOnly[1]),
      Number(isoDateOnly[2]) - 1,
      Number(isoDateOnly[3]),
    );
  }
  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatBillingPeriodRange(
  start: string | undefined | null,
  end: string | undefined | null,
): string {
  const startDate = start ? parseApiDate(start) : null;
  const endDate = end ? parseApiDate(end) : null;
  const opts: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  };
  if (startDate && endDate) {
    return `${startDate.toLocaleDateString('en-US', opts)} – ${endDate.toLocaleDateString('en-US', opts)}`;
  }
  if (startDate) return startDate.toLocaleDateString('en-US', opts);
  if (endDate) return endDate.toLocaleDateString('en-US', opts);
  return '';
}

/** YYYY-MM-DD for facility-cancellation payload */
function toCancellationEndDate(record: BillingRecord): string {
  const iso = record.planEndDateIso;
  if (iso && typeof iso === 'string') {
    const m = iso.match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) return m[1];
    const d = new Date(iso);
    if (!Number.isNaN(d.getTime())) {
      const y = d.getFullYear();
      const mo = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${mo}-${day}`;
    }
  }
  const exp = record.expiryDate;
  if (exp && exp.includes('/')) {
    const parts = exp.split('/');
    if (parts.length === 3) {
      const [mm, dd, yy] = parts;
      return `${yy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
    }
  }
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
}

function firstDefinedNum(...vals: unknown[]): number | undefined {
  for (const v of vals) {
    const n = pickNumericId(v);
    if (n !== undefined) return n;
  }
  return undefined;
}

/**
 * Resolves cancellation payload IDs from the billing row plus session facility context.
 */
function nestedNumericId(
  value: unknown,
  key: string,
): number | undefined {
  if (!value || typeof value !== 'object') return undefined;
  return pickNumericId((value as Record<string, unknown>)[key]);
}

function resolveFacilityCancellationIds(record: BillingRecord): {
  user_associated_facility_id: number;
  facility_id: number;
  plan_id: number;
  plan_purchased_id: number;
} | null {
  const sessionF = getSelectedFacilityFromSession() as Record<string, unknown> | null;
  const raw = record.rawPlanRow ?? {};

  const userAssociatedFacilityId = firstDefinedNum(
    record.userAssociatedFacilityId,
    raw.user_associated_facility_id,
    raw.userAssociatedFacilityId,
    raw.user_facility_id,
    raw.association_id,
    sessionF?.user_associated_facility_id,
    sessionF?.association_id,
    sessionF?.user_facility_association_id,
  );

  const facilityId = firstDefinedNum(
    record.facilityId,
    raw.facility_id,
    raw.facilityId,
    nestedNumericId(raw.facility, 'id'),
    sessionF?.facility_id,
    sessionF?.facilityId,
    sessionF?.id,
  );

  const recordAny = record as BillingRecord & Record<string, unknown>;

  const planId = firstDefinedNum(
    record.planId,
    raw.plan_id,
    raw.planId,
    raw.plan_master_id,
    raw.master_plan_id,
    nestedNumericId(raw.plan, 'id'),
    recordAny.plan_master_id,
    recordAny.master_plan_id,
  );

  const planPurchasedId = firstDefinedNum(
    record.planPurchasedId,
    raw.plan_purchased_id,
    raw.planPurchasedId,
    raw.plan_purchase_id,
    raw.purchased_plan_id,
    raw.subscription_id,
    raw.facility_plan_subscription_id,
    raw.id,
    recordAny.plan_purchase_id,
    recordAny.subscription_id,
    record.sourcePlanRowId,
  );

  if (
    userAssociatedFacilityId == null ||
    facilityId == null ||
    planId == null ||
    planPurchasedId == null
  ) {
    return null;
  }

  return {
    user_associated_facility_id: userAssociatedFacilityId,
    facility_id: facilityId,
    plan_id: planId,
    plan_purchased_id: planPurchasedId,
  };
}

const BillingPage: React.FC<BillingPageProps> = () => {
  const navigate = useNavigate();
  const facilityLayout = useFacilityLayoutDataOptional();
  // Keep header and row column widths in sync
  const columnWidths = {
    /** Facility Name — 20% narrower than prior 340px */
    facility: '272px',
    plan: '200px',
    /** Billing Frequency — 50% of prior widths */
    billingFrequencyHistory: '100px',
    billingFrequencyActive: '90px',
    billingPeriod: '180px',
    nextBilling: '170px',
    paymentMethod: '120px',
    status: '170px',
    amount: '120px',
    action: TABLE_ACTION_COLUMN_WIDTH,
  } as const;

  const [activeTab, setActiveTab] = useState<number>(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedAddonId = searchParams.get('addon');
  // TEMP: force-hide Add-ons tab. Set false → true later to restore settings-driven visibility.
  const TEMP_HIDE_BILLING_ADDONS_TAB = true;
  const [addonsEnabled, setAddonsEnabled] = useState(!TEMP_HIDE_BILLING_ADDONS_TAB);

  useEffect(() => {
    if (TEMP_HIDE_BILLING_ADDONS_TAB) {
      setAddonsEnabled(false);
      return;
    }
    let cancelled = false;
    getBillingAddonsSettings()
      .then((settings) => {
        if (!cancelled) setAddonsEnabled(settings.enabled !== false);
      })
      .catch(() => {
        if (!cancelled) setAddonsEnabled(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // #10853: Add-ons between Active Subscriptions and Billing History; URL tab persistence.
  const addonsTabIndex = 1;
  const historyTabIndex = addonsEnabled ? 2 : 1;

  useEffect(() => {
    const tab = (searchParams.get('tab') || '').toLowerCase();
    if (tab === 'addons') {
      if (addonsEnabled) {
        setActiveTab(addonsTabIndex);
      } else {
        setActiveTab(0);
        const next = new URLSearchParams(searchParams);
        next.set('tab', 'subscriptions');
        next.delete('addon');
        next.delete('step');
        next.delete('tier');
        next.delete('oigAddon');
        setSearchParams(next, { replace: true });
      }
      return;
    }
    if (tab === 'history' || tab === 'billing-history') {
      setActiveTab(historyTabIndex);
      return;
    }
    if (tab === 'subscriptions' || tab === 'active' || tab === '') {
      // Keep add-on deep links on the Add-ons tab even if tab param briefly missing.
      if (addonsEnabled && (selectedAddonId || searchParams.get('oigAddon') === '1')) {
        setActiveTab(addonsTabIndex);
        return;
      }
      if (tab === 'subscriptions' || tab === 'active') setActiveTab(0);
    }
  }, [searchParams, addonsEnabled, selectedAddonId, setSearchParams]);

  const handleBillingTabChange = (_: React.SyntheticEvent, value: number) => {
    setActiveTab(value);
    const next = new URLSearchParams(searchParams);
    if (addonsEnabled && value === addonsTabIndex) {
      next.set('tab', 'addons');
    } else if (value === historyTabIndex) {
      next.set('tab', 'history');
      next.delete('addon');
      next.delete('step');
      next.delete('tier');
      next.delete('oigAddon');
    } else {
      next.set('tab', 'subscriptions');
      next.delete('addon');
      next.delete('step');
      next.delete('tier');
      next.delete('oigAddon');
    }
    setSearchParams(next, { replace: true });
  };

  const userEmail = React.useMemo(() => {
    try {
      const raw = localStorage.getItem('user');
      if (!raw) return '';
      const user = JSON.parse(raw) as Record<string, unknown>;
      return (user['email'] as string) || '';
    } catch {
      return '';
    }
  }, []);
  const [billingRecords, setBillingRecords] = useState<BillingRecord[]>([]);
  const [loading, setLoading] = useState(() => {
    if (typeof window === 'undefined') return true;
    const params = new URLSearchParams(window.location.search);
    const tab = (params.get('tab') || '').toLowerCase();
    return !(tab === 'addons' || params.get('addon') || params.get('oigAddon') === '1');
  });
  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(5);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [actionAnchorEl, setActionAnchorEl] = useState<null | HTMLElement>(null);
  const [actionMenuRecord, setActionMenuRecord] = useState<BillingRecord | null>(null);
  const [confirmCancellationOpen, setConfirmCancellationOpen] = useState(false);
  const [subscriptionCancelledOpen, setSubscriptionCancelledOpen] = useState(false);
  const [resubscribeOpen, setResubscribeOpen] = useState(false);
  const [welcomeBackOpen, setWelcomeBackOpen] = useState(false);
  const [cancelledSubscriptionRecord, setCancelledSubscriptionRecord] =
    useState<BillingRecord | null>(null);
  const [reviewSubscriptionOpen, setReviewSubscriptionOpen] = useState(false);
  const [subscriptionForCancel, setSubscriptionForCancel] = useState<BillingRecord | null>(null);
  const [cancelReason, setCancelReason] = useState<string>('');
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [isUpdatingPaymentMethod, setIsUpdatingPaymentMethod] = useState(false);
  const [resubscribeSubmitting, setResubscribeSubmitting] = useState(false);
  /** Keeps the row for cancel when MUI Menu onClose clears React state before MenuItem click runs */
  const pendingCancelRecordRef = useRef<BillingRecord | null>(null);
  /** Keeps cancel row through async confirm + success dialog */
  const subscriptionForCancelRef = useRef<BillingRecord | null>(null);
  const billingLoadGenRef = useRef(0);

  const formatDate = (value: string | Date | undefined | null): string => {
    if (!value) return '';
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  // Log page access to audit log
  useEffect(() => {
    const logPageAccess = async () => {
      const userId = parseInt(getCurrentUserId(), 10);
      const facility = getSelectedFacilityFromSession();
      await auditLogService.logApiCallToAuditLog({
        user_id: userId,
        endpoint: '/dashboard/billing',
        method: 'GET',
        request_payload: {},
        response_payload: {},
        status_code: 200,
        facility_info: facility
          ? {
              facility_id: facility.facility_id,
              facility_name: facility.facility_name,
            }
          : {},
        action_details: {
          page: 'Billings',
          action: 'VIEW',
        },
        action_type: 'VIEW',
        action_status: 'SUCCESS',
      });
    };
    logPageAccess();
  }, []);

  useEffect(() => {
    // Skip only when Add-ons tab is active — stale ?tab=addons must not block
    // subscriptions/history after the user switches tabs.
    if (addonsEnabled && activeTab === addonsTabIndex) {
      setLoading(false);
      return;
    }
    // Active Subscriptions: client-side page slice — reload on tab/size only.
    // Billing History: server-side page — reload on page too.
    loadBillingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab === 0 ? rowsPerPage : page, rowsPerPage, activeTab, addonsEnabled]);

  const loadBillingData = async (options?: { silent?: boolean }) => {
    const silent = options?.silent === true;
    const loadGen = ++billingLoadGenRef.current;
    try {
      if (!silent) {
        setLoading(true);
      }
      // Determine user id
      let userIdStr = '0';
      try {
        const userRaw = localStorage.getItem('user');
        if (userRaw) {
          const user = JSON.parse(userRaw) as any;
          userIdStr = String(user?.id ?? user?.user_id ?? '0');
        }
      } catch {}

      let rows: any[] = [];
      let total = 0;

      if (activeTab === 0) {
        // Plans API nests customers→plans and often returns the full set (page/size
        // are not reliable plan-row pagination). Fetch once, flatten, page client-side.
        const response: any = await billingService.getPlans({
          page: 1,
          size: 100,
          user_id: userIdStr,
          filters: {},
        });

        const resultArray: any[] = Array.isArray(response?.result) ? response.result : [];
        rows = resultArray.flatMap((customer: any) => {
          const plans: any[] = Array.isArray(customer?.plans) ? customer.plans : [];
          return plans.map((plan: any) => ({
            ...plan,
            // Prefer plan-level stamp only — customer.facility_name collides when one customer_id has many facilities
            facility_name: plan?.facility_name,
            facility_id: plan?.facility_id,
            customer_name: customer?.customer_name,
            user_associated_facility_id:
              plan.user_associated_facility_id ??
              customer?.user_associated_facility_id ??
              customer?.user_associated_facilityId ??
              customer?.association_id,
          }));
        });

        // Full flattened list already in memory; page/size ignored upstream.
        // Do not prefer a future total_count (may count customers, not plans).
        total = rows.length;
      } else {
        // Other tabs (e.g. Billing History) can continue to use invoices API
        const response: any = await billingService.getInvoices({
          page,
          size: rowsPerPage,
          user_id: userIdStr,
          filters: {},
        });
        const result = response?.result || {};
        rows = Array.isArray((result as any).data) ? (result as any).data : [];
        total = Number((result as any).total_count ?? rows.length);
      }

      if (loadGen !== billingLoadGenRef.current) {
        return;
      }

      setTotalCount(Number.isFinite(total) ? total : rows.length);

      if (Array.isArray(rows) && rows.length > 0) {
        const mapped: BillingRecord[] = rows.map((r: any, idx: number) => {
          const rawStatus = String(r.status ?? '').toLowerCase().trim();
          const rowCancelled = isApiCancelledRow(r as Record<string, unknown>);
          const isActivePlan =
            activeTab === 0
              ? !rowCancelled &&
                (rawStatus === 'active' || Number(r.is_active) === 1)
              : rawStatus === 'paid' ||
                rawStatus === 'success' ||
                rawStatus === 'active';

          return {
            id: String(r.id ?? idx + 1),
            facilityName: r.facility_name ?? 'Facility',
            selectedPlan: r.plan_name ?? 'Plan',
            status: (isActivePlan ? 'Active' : 'Payment Required') as 'Active' | 'Inactive',
            apiStatus: r.status != null ? String(r.status) : null,
            cancellationDate:
              r.cancellation_date ??
              r.cancellation_end_date ??
              r.cancel_at ??
              null,
            cancelAtPeriodEnd:
              Number(r.cancel_at_period_end) === 1 || r.cancel_at_period_end === true,
            isCancelled:
              rowCancelled ||
              Number(r.is_cancelled) === 1 ||
              r.is_cancelled === true,
            invoiceDate: formatDate(r.billing_date ?? r.created_date ?? ''),
            expiryDate: formatDate(r.plan_end_date ?? ''),
            invoiceId: String(r.invoice_code ?? r.id ?? ''),
            invoiceUrl: typeof r.url === 'string' ? r.url : undefined,
            billingFrequency: r.billing_frequency
              ?? r.frequency_name
              ?? (String(r.plan_interval || '').toLowerCase() === 'yearly' ? 'Yearly' : 'Monthly'),
            paymentMethod: r.payment_method || r.card_type || '',
            facilityId: pickNumericId(
              r.facility_id ?? r.facilityId ?? r.facility?.id,
            ),
            ...(activeTab === 0
              ? {
                  userAssociatedFacilityId: pickNumericId(
                    r.user_associated_facility_id ??
                      r.userAssociatedFacilityId ??
                      r.user_facility_id ??
                      r.association_id,
                  ),
                  customerId: pickNumericId(r.customer_id ?? r.customerId),
                  planId: pickNumericId(
                    r.plan_id ??
                      r.planId ??
                      r.plan_master_id ??
                      r.master_plan_id ??
                      r.plan?.id,
                  ),
                  planPurchasedId: pickNumericId(
                    r.plan_purchased_id ??
                      r.planPurchasedId ??
                      r.plan_purchase_id ??
                      r.purchased_plan_id ??
                      r.subscription_id ??
                      r.facility_plan_subscription_id ??
                      r.id,
                  ),
                  sourcePlanRowId: pickNumericId(r.id),
                  rawPlanRow: r as Record<string, unknown>,
                  planEndDateIso:
                    r.plan_end_date != null ? String(r.plan_end_date) : null,
                }
              : {}),
            planStartDate:
              r.plan_start_date != null
                ? String(r.plan_start_date)
                : r.planStartDate != null
                  ? String(r.planStartDate)
                  : null,
            planEndDate:
              r.plan_end_date != null
                ? String(r.plan_end_date)
                : r.planEndDate != null
                  ? String(r.planEndDate)
                  : null,
            billingPeriodDisplay: formatBillingPeriodRange(
              r.plan_start_date ?? r.planStartDate,
              r.plan_end_date ?? r.planEndDate,
            ),
            // Extra fields for Billing History tab (kept as loose props)
            _rawStatus: r.status,
            total_amount: r.total_amount ?? r.plan_price,
          } as BillingRecord & { _rawStatus?: string; total_amount?: number };
        });
        setBillingRecords(applySubscriptionDisplayState(mapped));
      } else {
        // No rows from API
        setBillingRecords([]);
        setTotalCount(0);
      }
    } catch (error) {
      if (loadGen !== billingLoadGenRef.current) {
        return;
      }
      console.error('Error loading billing data:', error);
      // On error, show empty state
      setBillingRecords([]);
      setTotalCount(0);
    } finally {
      if (!silent && loadGen === billingLoadGenRef.current) {
        setLoading(false);
      }
    }
  };

  // Pagination calculations
  const totalItems = totalCount;
  const totalPages = Math.max(1, Math.ceil(totalItems / rowsPerPage));
  const currentPage = Math.min(page, totalPages);
  // Active Subscriptions: client slice. Billing History: server already paged.
  const paginatedRecords =
    activeTab === 0
      ? billingRecords.slice(
          (currentPage - 1) * rowsPerPage,
          currentPage * rowsPerPage,
        )
      : billingRecords;

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleRowsPerPageChange = (e: any) => {
    const value = Number(e.target.value);
    setRowsPerPage(value);
    setPage(1);
  };

  const openRowActionMenu = (event: React.MouseEvent<HTMLElement>, record: BillingRecord) => {
    pendingCancelRecordRef.current = record;
    setActionAnchorEl(event.currentTarget);
    setActionMenuRecord(record);
  };

  const closeRowActionMenu = () => {
    setActionAnchorEl(null);
    setActionMenuRecord(null);
    /* Intentionally keep pendingCancelRecordRef: Menu onClose may run before MenuItem onClick */
  };

  const openCancelDialog = () => {
    const rec = pendingCancelRecordRef.current ?? actionMenuRecord;
    if (rec) {
      setSubscriptionForCancel(rec);
      subscriptionForCancelRef.current = rec;
      setReviewSubscriptionOpen(true);
      setCancelReason('');
    } else {
      toast.error('Could not open cancellation. Please try the row action again.');
    }
    pendingCancelRecordRef.current = null;
    setActionAnchorEl(null);
    setActionMenuRecord(null);
  };

  const closeReviewSubscription = () => {
    setReviewSubscriptionOpen(false);
    setSubscriptionForCancel(null);
    subscriptionForCancelRef.current = null;
    setCancelReason('');
  };

  const continueToCancelFromReview = () => {
    setReviewSubscriptionOpen(false);
    setConfirmCancellationOpen(true);
  };

  const goBackToReviewFromConfirm = () => {
    setConfirmCancellationOpen(false);
    setReviewSubscriptionOpen(true);
  };

  const closeCancelFlow = () => {
    setConfirmCancellationOpen(false);
    setReviewSubscriptionOpen(false);
    setCancelReason('');
    setSubscriptionForCancel(null);
    subscriptionForCancelRef.current = null;
  };

  const closeSubscriptionCancelled = () => {
    setSubscriptionCancelledOpen(false);
    if (!resubscribeOpen && !welcomeBackOpen) {
      setCancelledSubscriptionRecord(null);
    }
  };

  const closeResubscribe = () => {
    setResubscribeOpen(false);
    if (!welcomeBackOpen) {
      setCancelledSubscriptionRecord(null);
    }
  };

  const closeWelcomeBack = () => {
    setWelcomeBackOpen(false);
    setCancelledSubscriptionRecord(null);
  };

  const openResubscribeFromGrid = (record: BillingRecord) => {
    setCancelledSubscriptionRecord(record);
    setResubscribeOpen(true);
  };

  const handleResubscribe = () => {
    setSubscriptionCancelledOpen(false);
    setResubscribeOpen(true);
  };

  const handleConfirmResubscription = async () => {
    if (!cancelledSubscriptionRecord) {
      toast.error('Missing subscription data for resubscription.');
      return;
    }
    const ids = resolveFacilityCancellationIds(cancelledSubscriptionRecord);
    if (!ids) {
      toast.error(
        'Missing subscription data for resubscription. Ensure a facility is selected, refresh billing, or contact support.',
      );
      return;
    }
    try {
      setResubscribeSubmitting(true);
      await submitFacilityResubscribe({
        user_associated_facility_id: ids.user_associated_facility_id,
        facility_id: ids.facility_id,
        plan_purchased_id: ids.plan_purchased_id,
      });
      facilityLayout?.patchMyFacilityRow(ids.facility_id, { is_restricted: false });
      void facilityLayout?.refreshMyFacilities({ bypassCache: true });
      setResubscribeOpen(false);
      setWelcomeBackOpen(true);
      void loadBillingData({ silent: true });
    } catch (error) {
      console.error('Facility resubscription failed:', error);
      toast.error(
        error instanceof Error ? error.message : 'Resubscription request failed.',
      );
    } finally {
      setResubscribeSubmitting(false);
    }
  };

  const handleUpdatePaymentMethod = async () => {
    const record = pendingCancelRecordRef.current ?? actionMenuRecord;
    if (!record) {
      toast.error('Could not open payment update. Please try the row action again.');
      return;
    }

    if (!record.customerId) {
      toast.error('Missing customer ID for payment update.');
      return;
    }

    try {
      setIsUpdatingPaymentMethod(true);
      const { returnUrl, cancelUrl } = getPaymentMethodUpdateReturnUrls();
      await startUpdatePaymentMethod(record.customerId, { returnUrl, cancelUrl });
    } catch (error: unknown) {
      setIsUpdatingPaymentMethod(false);
      toast.error(
        error instanceof Error
          ? error.message
          : 'Unable to update payment method. Please try again.',
      );
      pendingCancelRecordRef.current = null;
      closeRowActionMenu();
    }
  };

  const fetchInvoiceBlob = async (record: BillingRecord): Promise<Blob | null> => {
    if (record.invoiceId) {
      try {
        return await billingService.downloadInvoice(record.invoiceId);
      } catch {
        // fall back to invoiceUrl
      }
    }

    if (record.invoiceUrl) {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token') || '';
      const headers: Record<string, string> = {};
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch(record.invoiceUrl, { headers });
      if (!response.ok) return null;
      return response.blob();
    }

    return null;
  };

  const invoiceActionsAvailable =
    Boolean(actionMenuRecord?.invoiceId || actionMenuRecord?.invoiceUrl);

  const handleViewInvoice = async () => {
    const record = actionMenuRecord;
    if (!record) return;

    try {
      const blob = await fetchInvoiceBlob(record);
      if (!blob) {
        toast.error('Invoice is not available to view.');
        return;
      }

      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch {
      toast.error('Unable to open invoice.');
    } finally {
      closeRowActionMenu();
    }
  };

  const handleDownloadInvoice = async () => {
    const record = actionMenuRecord;
    if (!record) return;

    try {
      const blob = await fetchInvoiceBlob(record);
      if (!blob) {
        toast.error('Invoice is not available to download.');
        return;
      }

      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `invoice-${record.invoiceId || 'download'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error('Unable to download invoice.');
    } finally {
      closeRowActionMenu();
    }
  };

  const handleConfirmCancellation = async () => {
    const record = subscriptionForCancelRef.current ?? subscriptionForCancel;
    if (!record) {
      toast.error('Missing subscription data for cancellation.');
      return;
    }
    const ids = resolveFacilityCancellationIds(record);
    if (!ids) {
      toast.error(
        'Missing subscription data for cancellation. Ensure a facility is selected, refresh billing, or contact support.',
      );
      return;
    }
    try {
      setCancelSubmitting(true);
      await submitFacilityCancellation({
        ...ids,
        cancellation_reason: cancelReason || 'Not specified',
        end_date: toCancellationEndDate(record),
      });
      const cancellationEndDate = toCancellationEndDate(record);
      const cancelledRecord = withCancelledApiFields(record, cancellationEndDate);
      setBillingRecords((prev) =>
        applySubscriptionDisplayState(
          prev.map((row) => (row.id === record.id ? cancelledRecord : row)),
        ),
      );
      setCancelledSubscriptionRecord(cancelledRecord);
      setConfirmCancellationOpen(false);
      setReviewSubscriptionOpen(false);
      setCancelReason('');
      setSubscriptionForCancel(null);
      subscriptionForCancelRef.current = null;
      // Immediate restrict: stamp sidebar + wall before my-facilities refetch lands
      facilityLayout?.patchMyFacilityRow(ids.facility_id, { is_restricted: true });
      void facilityLayout?.refreshMyFacilities({ bypassCache: true });
      void loadBillingData({ silent: true });
      navigateToRestrictedFacility(
        navigate,
        {
          facility_id: ids.facility_id,
          facility_name: record.facilityName,
          plan_id: ids.plan_id,
          is_restricted: true,
        },
        (facilityLayout?.userFacilities?.length ?? 0) > 1,
      );
    } catch (error) {
      console.error('Facility cancellation failed:', error);
      toast.error(
        error instanceof Error ? error.message : 'Cancellation request failed.',
      );
    } finally {
      setCancelSubmitting(false);
    }
  };

  const billingDialogs = (
    <>
      <ReviewSubscriptionDialog
        open={reviewSubscriptionOpen}
        record={subscriptionForCancel}
        facilityId={
          subscriptionForCancel
            ? resolveFacilityCancellationIds(subscriptionForCancel)?.facility_id
            : null
        }
        accessEndDate={
          subscriptionForCancel
            ? toCancellationEndDate(subscriptionForCancel)
            : null
        }
        onKeep={closeReviewSubscription}
        onContinueToCancel={continueToCancelFromReview}
      />

      <ConfirmCancellationDialog
        open={confirmCancellationOpen}
        record={subscriptionForCancel}
        cancelReason={cancelReason}
        submitting={cancelSubmitting}
        onCancelReasonChange={setCancelReason}
        onGoBack={goBackToReviewFromConfirm}
        onConfirm={() => void handleConfirmCancellation()}
      />

      <SubscriptionCancelledDialog
        open={subscriptionCancelledOpen}
        record={cancelledSubscriptionRecord}
        onBackToBilling={closeSubscriptionCancelled}
        onResubscribe={handleResubscribe}
      />

      <ResubscribeDialog
        open={resubscribeOpen}
        record={cancelledSubscriptionRecord}
        submitting={resubscribeSubmitting}
        onBackToBilling={closeResubscribe}
        onConfirm={() => void handleConfirmResubscription()}
        onChangePaymentMethod={() =>
          toast.info('Payment method update is not available yet.')
        }
      />

      <WelcomeBackDialog
        open={welcomeBackOpen}
        record={cancelledSubscriptionRecord}
        onGoToBilling={closeWelcomeBack}
      />
    </>
  );

  if (loading && !subscriptionCancelledOpen && !resubscribeOpen && !welcomeBackOpen) {
    return (
      <>
      <PageContainer>
        <HeaderContainer>
          <HeaderTitleRow>
            <HeaderIconWrapper>
              <HeaderIcon />
            </HeaderIconWrapper>
            <Title variant="inherit">Payments and Billing</Title>
          </HeaderTitleRow>
        </HeaderContainer>
        <ContentContainer>
          <Typography sx={{ fontFamily: 'Rethink Sans, sans-serif' }}>Loading billing data...</Typography>
        </ContentContainer>
      </PageContainer>
      {billingDialogs}
    </>
    );
  }

  return (
    <>
    <PageContainer>
      <HeaderContainer>
        <HeaderTitleRow>
          <HeaderIconWrapper>
            <HeaderIcon />
          </HeaderIconWrapper>
          <Title variant="inherit">Payments and Billing</Title>
        </HeaderTitleRow>
      </HeaderContainer>

      <ContentContainer>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Typography sx={{ fontSize: '18px', fontFamily: 'Rethink Sans, sans-serif', color: '#065b70', mb: '4px' }}>
            All facilities listed are attached to{' '}
            <Box component="span" sx={{ fontWeight: 600 }}>
              {userEmail || 'your account'}
            </Box>
          </Typography>
          <Tabs
            value={activeTab}
            onChange={handleBillingTabChange}
            sx={{
              minHeight: '38px',
              borderBottom: '1px solid #d8e8ec',
              '& .MuiTab-root': {
                minHeight: '38px',
                textTransform: 'none',
                fontFamily: 'Rethink Sans, sans-serif',
                fontSize: '18px',
                color: '#5d5d5d',
                px: 1.5,
              },
              '& .Mui-selected': {
                color: '#065b70 !important',
                fontWeight: 600,
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#0a829d',
                height: 2,
              },
            }}
          >
            <Tab label="Active Subscriptions" />
            {addonsEnabled ? <Tab label="Add-ons" /> : null}
            <Tab label="Billing History" />
          </Tabs>
          {addonsEnabled && activeTab === addonsTabIndex ? (
            searchParams.get('oigAddon') === '1' ? (
              <AddonPurchaseSuccess />
            ) : selectedAddonId && searchParams.get('step') === '2' ? (
              <TierReview addonId={selectedAddonId} tierId={searchParams.get('tier') || ''} />
            ) : selectedAddonId && searchParams.get('step') === '1' ? (
              <TierPicker addonId={selectedAddonId} />
            ) : selectedAddonId ? <AddonDetail addonId={selectedAddonId} /> : <AddonsList />
          ) : (
          <TableContainer>
            <Table>
              {/* Table Header */}
              <TableHeader>
                <TableHeaderCell width={columnWidths.facility}>Facility Name</TableHeaderCell>
                {activeTab === 0 ? (
                  <>
                    <TableHeaderCell width={columnWidths.plan}>Plan</TableHeaderCell>
                    <TableHeaderCell width={columnWidths.billingFrequencyActive}>
                      Billing Frequency
                    </TableHeaderCell>
                    <TableHeaderCell width={columnWidths.nextBilling}>Next Billing Date</TableHeaderCell>
                    <TableHeaderCell width={columnWidths.status}>Status</TableHeaderCell>
                    <TableHeaderCell width={columnWidths.paymentMethod}>
                      Payment Method
                    </TableHeaderCell>
                    <TableHeaderCell width={columnWidths.action} sx={actionHeaderSx}>
                      Action
                    </TableHeaderCell>
                  </>
                ) : (
                  <>
                    <TableHeaderCell width={columnWidths.plan}>Plan Name</TableHeaderCell>
                    <TableHeaderCell width={columnWidths.billingFrequencyHistory}>
                      Billing Frequency
                    </TableHeaderCell>
                    <TableHeaderCell width={columnWidths.billingPeriod}>
                      Latest Billing Period
                    </TableHeaderCell>
                    <TableHeaderCell width={columnWidths.nextBilling}>Last Invoice Date</TableHeaderCell>
                    <TableHeaderCell width={columnWidths.status}>Latest Invoice Status</TableHeaderCell>
                    <TableHeaderCell width={columnWidths.amount}>Amount</TableHeaderCell>
                    <TableHeaderCell width={columnWidths.action} sx={actionHeaderSx}>
                      Action
                    </TableHeaderCell>
                  </>
                )}
              </TableHeader>

              {/* Table Body */}
              <Box sx={{ paddingBottom: '8px' }}>
                {paginatedRecords.map((record) => (
                  <TableRow key={record.id}>
                    <TableCell width={columnWidths.facility}>
                      <Box sx={{ width: '100%' }}>
                        <Typography
                          onClick={() =>
                            navigate('/dashboard/MyFacility', {
                              state: {
                                currentView: 'MyFacility',
                                ...(record.facilityId != null
                                  ? { facilityId: record.facilityId }
                                  : {}),
                                ...(record.facilityName
                                  ? { facilityName: record.facilityName }
                                  : {}),
                              },
                            })
                          }
                          sx={{
                            fontSize: '12px',
                            fontFamily: 'Rethink Sans, sans-serif',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: '#065b70',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            textUnderlineOffset: '2px',
                          }}
                        >
                          {record.facilityName}
                        </Typography>
                      </Box>
                    </TableCell>

                    {activeTab === 0 ? (
                      <>
                        <TableCell width={columnWidths.plan}>
                          <PlanText>{record.selectedPlan}</PlanText>
                        </TableCell>

                        <TableCell width={columnWidths.billingFrequencyActive}>
                          <Typography sx={{ fontSize: '12px', fontFamily: 'Rethink Sans, sans-serif' }}>
                            {record.billingFrequency || 'Monthly'}
                          </Typography>
                        </TableCell>

                        <TableCell width={columnWidths.nextBilling}>
                          <Typography sx={{ fontSize: '12px', fontFamily: 'Rethink Sans, sans-serif' }}>
                            {formatLongDate(record.expiryDate)}
                          </Typography>
                        </TableCell>
                      </>
                    ) : (
                      <>
                        {/* Billing History tab */}
                        <TableCell width={columnWidths.plan}>
                          <PlanText>{record.selectedPlan}</PlanText>
                        </TableCell>

                        <TableCell width={columnWidths.billingFrequencyHistory}>
                          <Typography sx={{ fontSize: '12px', fontFamily: 'Rethink Sans, sans-serif' }}>
                            {record.billingFrequency || 'Monthly'}
                          </Typography>
                        </TableCell>

                        <TableCell width={columnWidths.billingPeriod}>
                          <Typography sx={{ fontSize: '12px', fontFamily: 'Rethink Sans, sans-serif' }}>
                            {record.billingPeriodDisplay || '—'}
                          </Typography>
                        </TableCell>

                        <TableCell width={columnWidths.nextBilling}>
                          <Typography sx={{ fontSize: '12px', fontFamily: 'Rethink Sans, sans-serif' }}>
                            {record.invoiceDate}
                          </Typography>
                        </TableCell>
                      </>
                    )}

                    <TableCell width={columnWidths.status}>
                      {activeTab === 0 ? (
                        (() => {
                          const subscriptionStatus =
                            record.subscriptionStatus ??
                            (record.status === 'Active' ? 'Active' : 'Payment Required');
                          if (subscriptionStatus === 'Cancelling') {
                            return (
                              <StatusChip
                                label={`Cancels – ${record.cancelsOnLabel || formatCancelChipDate(record.expiryDate)}`}
                                statusvariant="cancelling"
                              />
                            );
                          }
                          return (
                            <StatusChip
                              label={subscriptionStatus}
                              statusvariant={
                                subscriptionStatus === 'Active' ? 'active' : 'paymentRequired'
                              }
                            />
                          );
                        })()
                      ) : (
                        (() => {
                          const raw = String(
                            (record as any)._rawStatus ??
                              record.apiStatus ??
                              record.status ??
                              '',
                          )
                            .toLowerCase()
                            .trim();
                          // Multi-facility bug wrote invoice.status=PPD "active"; treat as paid.
                          const isPaid =
                            raw === 'paid' || raw === 'success' || raw === 'active';
                          const label = isPaid
                            ? 'Paid'
                            : raw === 'failed'
                              ? 'Failed'
                              : record.apiStatus ?? record.status;
                          return (
                            <StatusChip
                              label={label}
                              statusvariant={isPaid ? 'active' : 'paymentRequired'}
                            />
                          );
                        })()
                      )}
                    </TableCell>

                    {activeTab === 0 ? (
                      <>
                        <TableCell width={columnWidths.paymentMethod}>
                          <Typography sx={{ fontSize: '12px', fontFamily: 'Rethink Sans, sans-serif' }}>
                            {formatPaymentMethodDisplay(record.paymentMethod)}
                          </Typography>
                        </TableCell>
                        <TableCell width={columnWidths.action} sx={actionCellSx}>
                          {record.subscriptionStatus === 'Cancelling' ? (
                            <Typography
                              component="button"
                              type="button"
                              onClick={() => openResubscribeFromGrid(record)}
                              sx={{
                                fontSize: '12px',
                                fontFamily: 'Rethink Sans, sans-serif',
                                fontWeight: 600,
                                color: '#0a829d',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                p: 0,
                                textDecoration: 'underline',
                                textUnderlineOffset: '2px',
                              }}
                            >
                              Resubscribe
                            </Typography>
                          ) : record.subscriptionStatus === 'Active' ? (
                            <ActionButton
                              size="small"
                              aria-label="Row actions"
                              aria-haspopup="true"
                              onClick={(e) => openRowActionMenu(e, record)}
                            >
                              <MoreHorizIcon />
                            </ActionButton>
                          ) : (
                            <Typography
                              sx={{
                                fontSize: '12px',
                                fontFamily: 'Rethink Sans, sans-serif',
                                color: '#737373',
                              }}
                            >
                              —
                            </Typography>
                          )}
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell width={columnWidths.amount}>
                          <Typography sx={{ fontSize: '12px', fontFamily: 'Rethink Sans, sans-serif' }}>
                            {(() => {
                              const amt = Number((record as any).total_amount ?? 0);
                              if (!Number.isFinite(amt) || amt === 0) return '$0';
                              return amt.toLocaleString('en-US', {
                                style: 'currency',
                                currency: 'USD',
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 2,
                              });
                            })()}
                          </Typography>
                        </TableCell>
                        <TableCell width={columnWidths.action} sx={actionCellSx}>
                          <ActionButton
                            size="small"
                            aria-label="Row actions"
                            aria-haspopup="true"
                            onClick={(e) => openRowActionMenu(e, record)}
                          >
                            <MoreHorizIcon />
                          </ActionButton>
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))}

                {/* Pagination row inside table border */}
                <TableRow>
                  <TableCell
                    style={{ flex: 1 }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 2,
                        flexWrap: 'wrap',
                        py: 0.5,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Select
                          size="small"
                          value={rowsPerPage}
                          onChange={handleRowsPerPageChange}
                          sx={{
                            minWidth: 80,
                            width: 80,
                            height: 28,
                            fontFamily: 'Rethink Sans, sans-serif',
                            '& .MuiSelect-select': {
                              padding: '4px 8px',
                              fontSize: 16,
                              display: 'flex',
                              alignItems: 'center',
                            },
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                '& .MuiMenuItem-root': {
                                  fontSize: 16,
                                  minHeight: '28px',
                                  lineHeight: '28px',
                                  fontFamily: 'Rethink Sans, sans-serif',
                                },
                              },
                            },
                          }}
                        >
                          <MenuItem value={5}>5</MenuItem>
                          <MenuItem value={10}>10</MenuItem>
                          <MenuItem value={20}>20</MenuItem>
                          
                        </Select>
                        <Pagination
                          count={totalPages}
                          page={currentPage}
                          onChange={handlePageChange}
                          color="primary"
                          size="small"
                          sx={{
                            '& .MuiPaginationItem-root': {
                              fontSize: 16,
                              height: 32,
                              minWidth: 32,
                              padding: '0 6px',
                              color: '#0a829d',
                            },
                            '& .MuiPaginationItem-root.Mui-selected': {
                              backgroundColor: '#e8f7fb',
                              border: '1px solid #0a829d',
                              color: '#0a829d',
                            },
                            '& .MuiPaginationItem-root.Mui-selected:hover': { backgroundColor: '#e8f7fb' },
                          }}
                        />
                      </Box>
                    </Box>
                  </TableCell>
                </TableRow>
              </Box>
            </Table>
          </TableContainer>
          )}
        </Box>
      </ContentContainer>

      <Menu
        anchorEl={actionAnchorEl}
        open={Boolean(actionAnchorEl)}
        onClose={closeRowActionMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              border: '1px solid #d8eff4',
              borderRadius: '8px',
              boxShadow: '0px 4px 12px rgba(0,0,0,0.06)',
              minWidth: 200,
            },
          },
        }}
      >
        {activeTab === 0 && (
          <>
            <MenuItem
              disabled={isUpdatingPaymentMethod}
              onClick={(e) => {
                e.stopPropagation();
                handleUpdatePaymentMethod();
              }}
              sx={{ fontFamily: 'Rethink Sans, sans-serif', fontSize: '14px', color: '#262626' }}
            >
              Update Payment Method
            </MenuItem>
            <MenuItem
              disabled={CANCEL_SUBSCRIPTION_DISABLED}
              onClick={(e) => {
                e.stopPropagation();
                openCancelDialog();
              }}
              sx={{ fontFamily: 'Rethink Sans, sans-serif', fontSize: '14px', color: '#a4262c' }}
            >
              Cancel Subscription
            </MenuItem>
          </>
        )}
        {activeTab === historyTabIndex && (
          <>
            <MenuItem
              onClick={handleViewInvoice}
              disabled={!invoiceActionsAvailable}
              sx={{ fontFamily: 'Rethink Sans, sans-serif', fontSize: '14px', color: '#262626' }}
            >
              View invoice
            </MenuItem>
            <MenuItem
              onClick={handleDownloadInvoice}
              disabled={!invoiceActionsAvailable}
              sx={{ fontFamily: 'Rethink Sans, sans-serif', fontSize: '14px', color: '#262626' }}
            >
              Download invoice
            </MenuItem>
          </>
        )}
      </Menu>
    </PageContainer>
    {billingDialogs}
    </>
  );
};

export default BillingPage;
